import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';

function SearchableDropdown({ options, value, onChange, placeholder = 'Select...', allLabel = 'All', dark = false, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  // Calculate menu position based on trigger element
  const updateMenuPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update position when open and on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updateMenuPos();
    const handleScrollOrResize = () => updateMenuPos();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updateMenuPos]);

  // Normalize options to {value, label} format
  const normalizedOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter(opt =>
    opt.label.toLowerCase().includes(searchText.toLowerCase())
  );

  // Find the label for the current value
  const selectedLabel = normalizedOptions.find(opt => opt.value === value)?.label || '';

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearchText('');
  };

  const handleInputClick = () => {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // A dependent dropdown can be disabled while its menu is open — clearing the
  // brand filter disables the model one — so close it rather than leaving an
  // orphaned popover the trigger can no longer dismiss.
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
      setSearchText('');
    }
  }, [disabled, isOpen]);

  const menuContent = isOpen && !disabled ? ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={`searchable-dropdown-menu ${dark ? 'searchable-dropdown-dark' : ''}`}
      style={{
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        zIndex: 99999
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className="searchable-dropdown-search"
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="searchable-dropdown-options">
        <div
          className={`searchable-dropdown-option ${!value ? 'selected' : ''}`}
          onClick={() => handleSelect('')}
        >
          {allLabel}
        </div>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((opt) => (
            <div
              key={opt.value}
              className={`searchable-dropdown-option ${value === opt.value ? 'selected' : ''}`}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </div>
          ))
        ) : (
          <div className="searchable-dropdown-no-results">No results found</div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div
      className={`searchable-dropdown ${dark ? 'searchable-dropdown-dark' : ''}`}
      ref={containerRef}
      style={disabled ? { opacity: 0.55, pointerEvents: 'none' } : undefined}
    >
      <div className="searchable-dropdown-trigger" onClick={handleInputClick}>
        <span className={value ? 'searchable-dropdown-value' : 'searchable-dropdown-placeholder'}>
          {selectedLabel || allLabel}
        </span>
        <span className="searchable-dropdown-arrow" style={{ display: 'flex' }}>
          <Icon
            name="chevronDown"
            size={14}
            style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          />
        </span>
      </div>
      {menuContent}
    </div>
  );
}

export default SearchableDropdown;
